import React, { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { AiFillDashboard } from 'react-icons/ai'
import { BiSolidBookContent } from 'react-icons/bi'
import { FaUserAlt, FaUsersCog } from 'react-icons/fa'
import PropTypes from 'prop-types'
import { Box, Drawer, Hidden, List, ListSubheader } from '@mui/material'
import NavItem from './NavItem'
import { AuthContext } from '../../../context/Auth'

const DRAWER_WIDTH = 256
const APP_BAR_HEIGHT = 70

const hasMatchingChild = (items = [], pathname) => {
  return items.some(item => {
    if (item.href && pathname.includes(item.href)) {
      return true
    }

    if (item.items?.length) {
      return hasMatchingChild(item.items, pathname)
    }

    return false
  })
}

const renderNavItems = ({ items, pathname, depth = 0, setSelectedTab }) => (
  <List disablePadding>
    {items?.map(item => {
      const key = item.title + depth
      const isActive = item.href
        ? pathname.includes(item.href)
        : hasMatchingChild(item.items, pathname)

      return item.items ? (
        <NavItem
          depth={depth}
          icon={item.icon}
          info={item.info}
          key={key}
          open={Boolean(isActive)}
          title={item.title}
          isActive={isActive}
        >
          {renderNavItems({
            depth: depth + 1,
            pathname,
            items: item.items,
            setSelectedTab
          })}
        </NavItem>
      ) : (
        <NavItem
          depth={depth}
          href={item.href}
          icon={item.icon}
          info={item.info}
          key={key}
          title={item.title}
          setSelectedTab={item => setSelectedTab(item)}
          isActive={isActive}
        />
      )
    })}
  </List>
)

const useStyles = theme => ({
  mobileDrawer: {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box'
  },
  desktopDrawer: {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box'
  }
})

const NavBar = ({ onMobileClose, openMobile, setSelectedTab }) => {
  const classes = useStyles()
  const location = useLocation()
  const Auth = useContext(AuthContext)
  const permissions = Auth?.userData?.permissions?.length > 0 ? Auth?.userData?.permissions[0] : []

  const userType = Auth?.userData?.userType

  const permissionMapping = {
    Dashboard: 'dashboard',
    'User Management': 'userManagement',
    'Customer Management': 'userManagement',
    'Business Management': 'userManagement',
    'Sub-Admin Management': 'userManagement',
    'Preference Menu Management': 'preferenceMenuManagement',
    'Venue Management': 'venueManagement',
    Offers: 'offersManagement',
    'Spontaneous Offers': 'offersManagement',
    'Existing Offers': 'offersManagement',
    Notifications: 'notificationManagement',
    Marketing: 'marketing',
    Reporting: 'reporting',
    Engagement: 'reporting',
    'Audit Log': 'reporting',
    'Feedback Management': 'feedbackManagement',
    'Content Management': 'contentManagement',
    'My Account': null
  }

  const sections = [
    {
      items: [
        {
          title: 'Dashboard',
          icon: AiFillDashboard,
          href: '/dashboard',
          tabview: 'Arbitrage'
        },
        {
          title: 'User Management',
          icon: FaUserAlt,
          items: [
            {
              title: 'Customer Management',
              icon: FaUsersCog,
              href: '/usermanagement/customer',
              tabview: 'Arbitrage'
            },
            {
              title: 'Business Management',
              icon: FaUsersCog,
              href: '/usermanagement/business',
              tabview: 'Arbitrage'
            },
            {
              title: 'Sub-Admin Management',
              icon: FaUsersCog,
              href: '/usermanagement/subadmin',
              tabview: 'Arbitrage'
            }
          ]
        },
        {
          title: 'Pref. Menu Management',
          icon: AiFillDashboard,
          href: '/prefrencemenu',
          tabview: 'Arbitrage'
        },
        {
          title: 'Venue Management',
          icon: BiSolidBookContent,
          href: '/venuelisting',
          tabview: 'Arbitrage'
        },
        {
          title: 'Offers',
          icon: BiSolidBookContent,
          tabview: 'Arbitrage',
          items: [
            {
              title: 'Spontaneous Offers',
              icon: BiSolidBookContent,
              href: '/offers/spontaneous',
              tabview: 'Arbitrage'
            },
            {
              title: 'Existing Offers',
              icon: BiSolidBookContent,
              href: '/offers/existing',
              tabview: 'Arbitrage'
            }
          ]
        },
        {
          title: 'Notifications',
          icon: BiSolidBookContent,
          items: [
            {
              title: 'Send notifications',
              icon: FaUsersCog,
              href: '/notificationManagement',
              tabview: 'Arbitrage'
            },
            {
              title: 'User Templates',
              icon: FaUsersCog,
              href: '/notificationManagement/consumer-templates',
              tabview: 'Arbitrage'
            },
            {
              title: 'Venue Templates',
              icon: FaUsersCog,
              href: '/notificationManagement/venue-templates',
              tabview: 'Arbitrage'
            }
          ]
        },

        {
          title: 'Marketing',
          icon: FaUserAlt,
          items: [
            {
              title: 'Marketing Insights',
              icon: FaUsersCog,
              href: '/marketing/marketingInsights',
              tabview: 'Arbitrage'
            },
            {
              title: 'Emails',
              icon: FaUsersCog,
              href: '/marketing/email',
              tabview: 'Arbitrage'
            },
            {
              title: 'Mailing Lists',
              icon: FaUsersCog,
              href: '/marketing/mailingLists',
              tabview: 'Arbitrage'
            }
          ]
        },

        {
          title: 'Reporting',
          icon: FaUserAlt,
          items: [
            {
              title: 'Notifications',
              icon: FaUsersCog,
              href: '/reporting/notifications',
              tabview: 'Arbitrage'
            },
            {
              title: 'Offer Redemptions',
              icon: FaUsersCog,
              href: '/reporting/offerredemptions',
              tabview: 'Arbitrage'
            },
            {
              title: 'Engagement',
              icon: FaUsersCog,
              href: '/reporting/engagement',
              tabview: 'Arbitrage'
            },
            {
              title: 'Audit Log',
              icon: FaUsersCog,
              href: '/reporting/audit-log',
              tabview: 'Arbitrage'
            }
          ]
        },
        {
          title: 'Feedback Management',
          icon: BiSolidBookContent,
          href: '/feedbacklisting',
          tabview: 'Arbitrage'
        },
        {
          title: 'Content Management',
          icon: BiSolidBookContent,
          href: '/contentlisting',
          tabview: 'Arbitrage'
        },
        {
          title: 'My Account',
          icon: AiFillDashboard,
          href: '/profile',
          tabview: 'Arbitrage'
        }
      ]
    }
  ]

  const filteredSections = sections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (userType === 'SUB_ADMIN') {
        const permissionKey = permissionMapping[item.title]
        return permissionKey === null || permissions[permissionKey] === true
      }
      return true
    })
  }))

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      sx={{
        background: 'linear-gradient(180deg, #f44169 0%, #f87995 100%)',
        borderRadius: { md: '14px' },
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.25,
          py: 1.5,
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.55)',
            borderRadius: '999px'
          }
        }}
      >
        <Box className="sideMenuBox">
          {filteredSections.map((section, i) => (
            <List
              key={`menu${i}`}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
                setSelectedTab
              })}
            </List>
          ))}
        </Box>
      </Box>
    </Box>
  )

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          open={openMobile}
          onClose={onMobileClose}
          variant="temporary"
          sx={{
            '& .MuiPaper-root': {
              width: DRAWER_WIDTH,
              padding: '8px',
              boxSizing: 'border-box',
              backgroundColor: '#ffffff',
              color: '#1a237e'
            }
          }}
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
          sx={{
            '& .MuiPaper-root': {
              top: `${APP_BAR_HEIGHT + 8}px`,
              left: '8px',
              backgroundColor: '#ffffff',
              color: '#1a237e',
              borderRadius: '14px',
              width: `${DRAWER_WIDTH - 8}px`,
              height: `calc(100dvh - ${APP_BAR_HEIGHT + 20}px)`,
              position: 'fixed',
              boxShadow: '0 20px 45px rgba(15, 23, 42, 0.2)',
              border: '1px solid rgba(255,255,255,0.15)',
              overflow: 'hidden'
            }
          }}
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  )
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  setSelectedTab: PropTypes.func.isRequired
}

export default NavBar
